import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Empty,
  notification,
  Pagination,
  Result,
  Spin,
  Modal,
} from "antd";
import { sampleApiCall } from "../../apiservice/authService";
import useFormatApiRequest from "../../hooks/formatApiRequest";
import { useAppDispatch } from "../../Redux/reduxCustomHook";
import { ILoadState } from "../../utils/loading.utils.";
import "./shared-Comp.css";
import { VideoCameraOutlined } from "@ant-design/icons";

type NotificationType = "success" | "info" | "warning" | "error";

type IGetStartedVideosListComp = {
  externalFilter?: any;
  initialDefaultFilter?: any;
  hidePagination?: boolean;
};

export const GetStartedVideosListComp: React.FC<IGetStartedVideosListComp> = ({
  externalFilter,
  initialDefaultFilter,
  hidePagination = false,
}) => {
  const [videosLoadState, setvideosLoadState] = useState<ILoadState>("loading");
  const [loadVideosData, setLoadvideosData] = useState(true);
  const [videosDefaultFilter, setvideosDefaultFilter] = useState(
    initialDefaultFilter || {}
  );
  const [tableData, setTableData] = useState<any[]>([]);
  const [api, contextHolder] = notification.useNotification();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<string>("");

  // Pagination Constant/Variables
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(1);
  const perPage = 10;

  // For Navigator/Redux
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Use Effect to reload API when External Filter has changed
  useEffect(() => {
    if (externalFilter) {
      setvideosDefaultFilter({
        ...videosDefaultFilter,
        ...externalFilter,
      });
      setLoadvideosData(true);
      setvideosLoadState("loading");
    }
  }, [externalFilter]);

  // A custom hook to Load All videos Details
  const videosDataResult = useFormatApiRequest(
    () => sampleApiCall(videosDefaultFilter),
    loadVideosData,
    () => {
      setLoadvideosData(false);
    },
    () => {
      processvideosResult();
    }
  );

  // Process The Current videos Data Result
  const processvideosResult = async () => {
    if (videosDataResult.httpState === "SUCCESS") {
      setTableData(videosDataResult.data?.data || []);
      setvideosLoadState("completed");
      setTotalItems(videosDataResult.data?.meta?.total || 10);
      if (!videosDataResult.data?.meta?.total) {
        setvideosLoadState("noData");
      }
    } else if (videosDataResult.httpState === "ERROR") {
      setvideosLoadState("error");
    } else if (videosDataResult.httpState === "LOADING") {
      setvideosLoadState("loading");
    }
  };

  // Show Notification
  const openNotificationWithIcon = (
    type: NotificationType,
    message: string,
    description: string,
    background?: string
  ) => {
    api[type]({
      message,
      description,
      placement: "bottomRight",
      style: { background },
    });
  };

  // Use to Control Pagination On Change Event
  const onPageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setvideosDefaultFilter({
      ...videosDefaultFilter,
      page: page,
      perPage: pageSize,
    });
    setLoadvideosData(true);
  };

  // Open video in modal
  const openVideoModal = (videoUrl: string) => {
    setCurrentVideo(videoUrl);
    setIsModalOpen(true);
  };

  return (
    <>
      {/* The context is used to hold the notification from ant design */}
      {contextHolder}
      <div className="grid pt-4">
        {/* Show Loading Indicator */}
        {videosLoadState === "loading" && (
          <div className="mt-2 mb-2 flex justify-center items-center pt-20 pb-20">
            <Spin size="large" />
          </div>
        )}

        {/* Show Loading Error */}
        {videosLoadState === "error" && (
          <div className="mt-2 mb-2">
            <Result
              status="500"
              title={<span className="text-gray-900 dark:text-stone-100">Error</span>}
              subTitle={
                <span className="text-gray-600 dark:text-stone-400">
                  Sorry, something went wrong, it could be a network related
                  error
                </span>
              }
              extra={
                <Button onClick={() => setLoadvideosData(true)} type="primary">
                  Reload
                </Button>
              }
            />
          </div>
        )}

        {/* Show No Data */}
        {videosLoadState === "noData" && (
          <div className="mt-8">
            <div className="flex justify-center mb-6">
              <span className="fontKanitRegular font-medium text-xl text-center text-gray-900 dark:text-stone-100">
                No Video Tutorials Available
                <br />
              </span>
            </div>
            <div className="flex justify-center items-center">
              <Empty
                image={
                  <VideoCameraOutlined
                    style={{ fontSize: 48 }} // Color will be inherited or set by AntD dark theme
                    className="text-gray-300 dark:text-stone-600"
                  />
                }
                description={
                  <span className="fontAlbertSansRegular text-center text-gray-600 dark:text-stone-400">
                    Once video tutorials are available, they will appear here.
                  </span>
                }
              />
            </div>
          </div>
        )}

        {/* Show Data */}
        {videosLoadState === "completed" && (
          <div className="mt-2">
            <div>
              {/* Videos */}
              <div className="grid grid-cols-1 gap-4">
                {tableData.map((video, index) => (
                  <div
                    key={index}
                    onClick={() => openVideoModal(video.videoUrl)}
                    className="cursor-pointer"
                  >
                    {/* Cards */}
                    <div className="p-4 rounded-lg border border-gray-300 dark:border-stone-700 shadow-lg bg-white dark:bg-stone-800">
                      <div className="flex items-center justify-center">
                        <img
                          style={{
                            width: "100%",
                            maxHeight: "200px",
                            objectFit: "cover",
                          }}
                          src={video.image}
                          alt="" // Should have a meaningful alt text
                        />
                      </div>
                      <div className="flex flex-col items-center justify-center pt-4">
                        <p className="font-sans text-sm text-gray-700 dark:text-stone-300">
                          {video.videoName}
                        </p>
                      </div>
                    </div>
                    {/* End Cards */}
                  </div>
                ))}
              </div>
              {/* End Videos */}
            </div>

            {!hidePagination && (
              <div className="grid mt-6">
                <Pagination
                  current={currentPage || 1}
                  onChange={onPageChange}
                  pageSize={perPage}
                  total={totalItems}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Video Modal */}
      <Modal
        title="Video Player"
        visible={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
      >
        <video width="100%" controls>
          <source src={currentVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </Modal>
    </>
  );
};
